/**
 * Prospect demo session store (docs/PROSPECT_DEMO_SANDBOX_PLAN.md §3.5.5).
 *
 * Holds the demo token issued by `POST /api/demo/session` after the shared
 * password was verified. Kept deliberately apart from the guardian auth:
 *
 * - stored under `sessionStorage['demo_token']`, never `auth_token`, so the
 *   two identities never mix (same tab-lifetime model as the guardian token);
 * - the password itself is never stored anywhere.
 *
 * Tiny framework-free store so both the lazily loaded demo tree and the
 * global Live Help button (mounted in App.tsx) can read it.
 */

import { useSyncExternalStore } from 'react';

export interface DemoQuota {
  extractions_remaining: number;
  max_pages: number;
  max_file_mb: number;
}

export interface DemoSession {
  token: string;
  /** Epoch ms. */
  expiresAt: number;
  sessionId: string;
  quota: DemoQuota;
}

export type DemoEndReason = 'expired' | 'ended' | null;

interface DemoState {
  session: DemoSession | null;
  endReason: DemoEndReason;
}

export const DEMO_TOKEN_STORAGE_KEY = 'demo_token';

const decodeSessionId = (token: string): string => {
  try {
    const payload = token.split('.')[1] || '';
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { sid?: string };
    return claims.sid || '';
  } catch {
    return '';
  }
};

const load = (): DemoSession | null => {
  try {
    const raw = sessionStorage.getItem(DEMO_TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DemoSession;
    if (!parsed?.token || typeof parsed.expiresAt !== 'number' || parsed.expiresAt <= Date.now()) {
      sessionStorage.removeItem(DEMO_TOKEN_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

let state: DemoState = { session: load(), endReason: null };
const listeners = new Set<() => void>();

const emit = (next: DemoState) => {
  state = next;
  listeners.forEach(l => l());
};

export const demoSessionStore = {
  getState: (): DemoState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** Store a freshly issued session (response of `POST /api/demo/session`). */
  start: (body: { demo_token: string; expires_at: string; quota: DemoQuota }): DemoSession => {
    const session: DemoSession = {
      token: body.demo_token,
      expiresAt: Date.parse(body.expires_at) || Date.now() + 60 * 60 * 1000,
      sessionId: decodeSessionId(body.demo_token),
      quota: body.quota,
    };
    try {
      sessionStorage.setItem(DEMO_TOKEN_STORAGE_KEY, JSON.stringify(session));
    } catch {
      // Private mode: the session simply will not survive a reload.
    }
    emit({ session, endReason: null });
    return session;
  },
  updateQuota: (quota: Partial<DemoQuota>): void => {
    if (!state.session) return;
    const session = { ...state.session, quota: { ...state.session.quota, ...quota } };
    try {
      sessionStorage.setItem(DEMO_TOKEN_STORAGE_KEY, JSON.stringify(session));
    } catch {
      /* ignore */
    }
    emit({ ...state, session });
  },
  /** Drop the session (expired / rejected token, or "End demo"). */
  end: (reason: Exclude<DemoEndReason, null>): void => {
    try {
      sessionStorage.removeItem(DEMO_TOKEN_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    if (!state.session && state.endReason === reason) return;
    emit({ session: null, endReason: reason });
  },
  /** A valid, unexpired token, or null. */
  token: (): string | null => {
    const s = state.session;
    return s && s.expiresAt > Date.now() ? s.token : null;
  },
};

/** React binding. */
export const useDemoSessionState = (): DemoState =>
  useSyncExternalStore(demoSessionStore.subscribe, demoSessionStore.getState, demoSessionStore.getState);

/** For tests only. */
export const __resetDemoSessionStoreForTests = (): void => {
  state = { session: load(), endReason: null };
  listeners.forEach(l => l());
};
